import _, { noop, first, isArray } from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef, useState, useContext, useMemo, useCallback, useEffect } from 'react';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withBannerService, CopilotRepositoryProvider } from '@bingads-webui/inline-copilot';
import { getAssetGroupStepStyles, getExtensionFormatters } from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { useWizardService, matchStepState, STEP_STATES } from '@bingads-webui-react/wizard';
import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import {
  ValidatedImageExperienceWrapper,
  hasImages,
  PerfMaxResponsiveAdPreviewUAPL,
} from '@bingads-webui-campaign-react/pmax-image-experience';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import {
  FormErrorGroup, UnmappedFormErrors as OriginalUnmappedFormErrors,
} from '@bingads-webui-campaign-react/form-error';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { AssetGroup } from '@bingads-webui/campaign-odata-schemas';
import { withScope } from '@bingads-webui-react/scope-service';
import { useFormService } from '@bingads-webui-react/form';
import { hasTextAssets } from '@bingads-webui-campaign-react/validated-text-collection';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { getFinalUrlWithPath } from '@bingads-webui-campaign-react/validated-url-input';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';

import {
  AssetEventFeature,
  useCopilotRecommendationSurvey,
} from '@bingads-webui-campaign-react/copilot-common';
import { useCopilot } from '@bingads-webui-campaign-react/copilot-experience';

import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useIsMounted } from '@bingads-webui-campaign-react/step-wizard-controls';
import { legacyI18nOfListingGroup } from '@bingads-webui-campaign-react/listing-groups-page';
import { AssetScenario } from '@bingads-webui-react/copilot-event-provider';

import { AssetGroupNameSection } from './components/asset-group-name-section';
import { AssetGroupNameSectionWithSocialSiteRecommendation } from './components/asset-group-name-section-with-social-site-recommendation';
import { AssetsSection } from './components/assets-section';
import { MoreOptionsSection } from './components/more-options-section';
import { EnhanceYourAdsSection } from './components/enhance-your-ads-section';
import { MoreAssetsSection } from './components/more-assets-section';
import { MoreSettingsSection } from './components/more-settings-section';
import { SignalSection } from './components/audience-signal-section';
import { getAssetsStepStyles } from './assets-step-styles';
import { STEP_PATHS } from '../step-constants';

const UnmappedFormErrors = withControlErrorHandling(OriginalUnmappedFormErrors);

const AssetsStepFormInternal = ({
  data,
  onDataChange: onDataChangeProp,
  error,
  scope,
}) => {
  const getStyles = useResponsiveStyles(getAssetsStepStyles);

  const isMounted = useIsMounted();

  const onDataChange = useCallback((...args) => {
    if (!isMounted.current) return;
    onDataChangeProp(...args);
  }, [isMounted, onDataChangeProp]);

  const {
    serverErrors = [],
    systemErrors = [],
  } = error || {};

  const {
    campaign,
    assetGroup,
  } = data;

  const activity = useContext(ActivityContext);
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const instrumentation = useContext(InstrumentationContext);
  const perfMarker = useContext(PerformanceMarkerContext);

  const imagesContainerRef = useRef(null);
  const logosContainerRef = useRef(null);

  const { state } = useWizardService();

  const { context: { wizardData } } = state;

  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap, legacyI18nOfListingGroup]), [i18n]);
  const legacyGlobalI18n = useMemo(() => window.globalLegacyI18n.get('$/i18n!legacy/global'), []);

  const mergedI18n = useMemo(() => {
    const mergedTempI18n = mergeI18n(
      legacyI18n,
      i18n
    );

    return mergeI18n(
      legacyGlobalI18n,
      mergedTempI18n
    );
  }, [i18n, legacyI18n, legacyGlobalI18n]);

  const {
    adExtensionsAssociationsFactory,
    adExtensionsDynamicFeatures,
  } = wizardData;

  const {
    wizardData: { flow },
  } = state.context;

  const { state: { context: { saveFailed } } } = useFormService();

  const classes = useDefaultStyles(getAssetGroupStepStyles);

  const isSaving = matchStepState(state, STEP_STATES.SAVING);
  const isCanceling = matchStepState(state, STEP_STATES.CANCELING);

  const { cid, aid } = scope.values || {};

  const {
    FinalUrls,
    Images,
    CallToAction,
    FinalUrlForRecommendation,
    BusinessName,
    videos = [],
    Headlines = {},
    LongHeadlines = {},
    Descriptions = {},
    extensionsAssociations = {},
    Path1,
    Path2,
    MobileFinalUrls,
    AssetGroupUrlTargets,
    UrlRulesOption,
  } = assetGroup || {};

  const {
    ProviderId,
    Id,
  } = campaign || {};

  const { Permissions, DynamicPermissions } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const {
    UAPLPMaxLogoPreviewEnabled, CopilotCTALogoGeneration, PmaxExtensionPreview,
    PMaxNewAspectRatios: isPMaxNewAspectRatiosEnabled,
    PMaxExcludeNewAspectRatios: isPMaxNewAspectRatiosExcluded,
    AdPreviewRefresh: isAdPreviewRefreshEnabled,
  } = permissions.dynamic;

  const isPMaxAspectRatiosV2Enabled = isPMaxNewAspectRatiosEnabled && !isPMaxNewAspectRatiosExcluded;

  const {
    triggerSurveyOnSubmit,
    triggerSurveyOnCancel,
  } = useCopilotRecommendationSurvey(AssetEventFeature.PMAX);

  const [recommendationContext] = useContext(RecommendationContext);

  const { postAssetEventLog } = recommendationContext || _.noop;

  const [selectedVideoId, setSelectedVideoId] = useState(0);

  const [finalUrlForCopilotRecommendations, setFinalUrlForCopilotRecommendations] = useState(first(FinalUrls) || FinalUrlForRecommendation);
  const [imageExperiencePortalContainerState, setImageExperiencePortalContainerState] = useState({});
  const [imageExperienceAddNewImageCallback, setImageExperienceAddNewImageCallback] = useState(noop);
  const [addNewDescriptionCallback, setAddNewDescriptionCallback] = useState(noop);
  const [addNewShortHeadlinesCallback, setAddNewShortHeadlinesCallback] = useState(noop);
  const [addNewLongHeadlinesCallback, setAddNewLongHeadlinesCallback] = useState(noop);
  const [newCTACallback, setNewCTACallback] = useState(noop);
  const [previewImagesSharedState, setPreviewImagesSharedState] = useState({
    previewImageId: null,
    previewLogoId: null,
    cropImageObj: null,
    editingPlacementType: '',
  });

  const onReset = useCallback(() => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroup: {
          ...prevData.assetGroup,
          Images: {
            ...assetGroup.Images,
            ImageList: [],
            ImageAssets: null,
          },
          Headlines: {
            Headline0: '',
            Headline1: '',
            Headline2: '',
          },
          LongHeadlines: {
            LongHeadline0: '',
          },
          Descriptions: {
            Description0: '',
            Description1: '',
          },
        },
      }),
      isValid: false,
    });
  }, [assetGroup, onDataChange]);

  const maxUploadNum = useMemo(() => {
    const imageListLength = Images && Images.ImageList && isArray(Images.ImageList) ? Images.ImageList.length : 0;
    return 20 - imageListLength;
  }, [Images]);

  const {
    copilotBanner,
    suggestionPoller,
    themes,
  } = useCopilot({
    maxUploadNum,
    addNewShortHeadlinesCallback,
    addNewLongHeadlinesCallback,
    addNewDescriptionCallback,
    imageExperienceAddNewImageCallback,
    newCTACallback,
    currentCTA: CallToAction,
    onReset,
    cid,
    aid,
    serverContext,
    instrumentation,
    data: assetGroup,
    isSaving,
    isCanceling,
    enableCopilotRecommendations: true,
    feature: AssetEventFeature.PMAX,
    assetScenario: AssetScenario.CREATE_WORK_FLOW,
  });

  const onFinalURLFocusOut = useCallback(() => {
    // to prevent sending too many requests, only update the copilot recommendations after user has finished editing URL
    setFinalUrlForCopilotRecommendations(FinalUrlForRecommendation);
  }, [FinalUrlForRecommendation]);

  const assetGroupNameSection = useMemo(() => (
    <>
      {permissions && permissions.dynamic && permissions.dynamic.SocialSitesRecommendation ? (
        <AssetGroupNameSectionWithSocialSiteRecommendation
          FinalUrls={FinalUrls}
          permissions={permissions}
          onFinalURLFocusOut={onFinalURLFocusOut}
          campaign={campaign}
        />
      ) : (
        <AssetGroupNameSection
          FinalUrls={FinalUrls}
          permissions={permissions}
          onFinalURLFocusOut={onFinalURLFocusOut}
          campaign={campaign}
        />
      )}
      {copilotBanner}
    </>
  ), [FinalUrls, campaign, copilotBanner, onFinalURLFocusOut, permissions]);

  const textAndImageAssetsOptional = useMemo(() => ProviderId && !hasImages(Images) && !hasTextAssets(Headlines, LongHeadlines, Descriptions), [Descriptions, Headlines, Images, LongHeadlines, ProviderId]);

  const updateFinalUrlCallback = useCallback((newFinalUrls) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroup: {
          ...prevData.assetGroup,
          FinalUrls: newFinalUrls,
        },
      }),
    });
  }, [onDataChange]);

  const updateExtensionsAssociations = useCallback((extensionType, associations) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroup: {
          ...prevData.assetGroup,
          extensionsAssociations: {
            ...prevData.assetGroup.extensionsAssociations,
            [extensionType]: associations,
          },
        },
      }),
    });
  }, [onDataChange]);

  const onUpdateImageList = useCallback((newImageList) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroup: {
          ...prevData.assetGroup,
          Images: {
            ...prevData.assetGroup.Images,
            ImageList: newImageList,
            ImageAssets: null,
          },
        },
      }),
    });
  }, [onDataChange]);

  const { suggestedObjectiveExtensions = [], otherObjectiveExtensions = [] } = useMemo(() =>
    flow !== STEP_PATHS.ASSETGROUPSETTING &&
    adExtensionsAssociationsFactory.getObjectiveExtensions(
      permissions,
      // TODO CHANGE OBJECTIVE
      { campaignType: 'PerformanceMax', campaignObjective: 'WebsiteConversions' }
    ), [flow, adExtensionsAssociationsFactory, permissions]);

  const assetsSection = useMemo(() => (
    <>
      <AssetsSection
        permissions={permissions}
        images={Images}
        videos={videos}
        perfMarker={perfMarker}
        businessName={BusinessName}
        headlines={Headlines}
        longHeadlines={LongHeadlines}
        descriptions={Descriptions}
        imagesContainerRef={imagesContainerRef}
        imageExperienceAddNewImageCallback={imageExperienceAddNewImageCallback}
        setAddNewShortHeadlinesCallback={setAddNewShortHeadlinesCallback}
        setAddNewLongHeadlinesCallback={setAddNewLongHeadlinesCallback}
        setAddNewDescriptionCallback={setAddNewDescriptionCallback}
        imageExperiencePortalContainerState={imageExperiencePortalContainerState}
        logosContainerRef={logosContainerRef}
        maxUploadNum={maxUploadNum}
        CallToAction={CallToAction}
        setNewCTACallback={setNewCTACallback}
        providerId={ProviderId}
        mergedI18n={mergedI18n}
        setSelectedVideoId={setSelectedVideoId}
        selectedVideoId={selectedVideoId}
      />
    </>
  ), [permissions, selectedVideoId, Images, mergedI18n, videos, perfMarker, BusinessName, Headlines, LongHeadlines, Descriptions, imageExperienceAddNewImageCallback, imageExperiencePortalContainerState, maxUploadNum, CallToAction, ProviderId]);

  const moreOptionsSection = useMemo(() => (
    <MoreOptionsSection
      permissions={permissions}
      finalUrls={FinalUrls}
      urlPath1={Path1}
      urlPath2={Path2}
      finalMobileUrls={MobileFinalUrls}
      urlRules={AssetGroupUrlTargets}
      urlRulesOption={UrlRulesOption}
      classes={classes}
    />
  ), [AssetGroupUrlTargets, FinalUrls, MobileFinalUrls, Path1, Path2, UrlRulesOption, classes, permissions]);

  const enhanceYourAdsSection = useMemo(() => (
    <EnhanceYourAdsSection
      suggestedObjectiveExtensions={suggestedObjectiveExtensions}
      adExtensionsAssociationsFactory={adExtensionsAssociationsFactory}
      adExtensionsDynamicFeatures={adExtensionsDynamicFeatures}
      onUpdateExtensionsAssociations={updateExtensionsAssociations}
      extensionsAssociations={extensionsAssociations}
      campaign={campaign}
    />
  ), [adExtensionsAssociationsFactory, adExtensionsDynamicFeatures, campaign, extensionsAssociations, suggestedObjectiveExtensions, updateExtensionsAssociations]);

  const moreAssetsSection = useMemo(() => (
    <MoreAssetsSection
      otherObjectiveExtensions={otherObjectiveExtensions}
      adExtensionsAssociationsFactory={adExtensionsAssociationsFactory}
      adExtensionsDynamicFeatures={adExtensionsDynamicFeatures}
      onUpdateExtensionsAssociations={updateExtensionsAssociations}
      extensionsAssociations={extensionsAssociations}
      classes={classes}
      campaign={campaign}
    />
  ), [adExtensionsAssociationsFactory, adExtensionsDynamicFeatures, campaign, classes, extensionsAssociations, otherObjectiveExtensions, updateExtensionsAssociations]);

  const moreSettingsSection = useMemo(() => (
    <MoreSettingsSection
      assetGroup={{ data: assetGroup }}
      campaignTimeZone={campaign && campaign.TimeZone}
    />
  ), [assetGroup, campaign]);

  const imageExperienceComponents = useMemo(() => (
    <div>
      <ValidatedImageExperienceWrapper
        i18n={i18n}
        permissions={permissions}
        imagesContaineRef={imagesContainerRef}
        logosContaineRef={logosContainerRef}
        imageAssetsOptional={textAndImageAssetsOptional}
        updateFinalUrlCallback={updateFinalUrlCallback}
        previewImagesSharedState={previewImagesSharedState}
        setPreviewImagesSharedState={setPreviewImagesSharedState}
        updatePortalContainerState={setImageExperiencePortalContainerState}
        setImageExperienceAddNewImageCallback={setImageExperienceAddNewImageCallback}
        validationProps={{ data: assetGroup, initialValue: assetGroup }}
        suggestionPoller={suggestionPoller}
        wizardPerfMarker={perfMarker}
        postAssetEventLog={postAssetEventLog}
        UAPLPMaxIntegration
        showCopilotOptionForImage={permissions.IsBackgroundSceneEnabled}
        isPMaxAspectRatiosV2Enabled={isPMaxAspectRatiosV2Enabled}
        isAdPreviewRefreshEnabled={isAdPreviewRefreshEnabled}
      />
    </div>), [i18n, permissions, textAndImageAssetsOptional, updateFinalUrlCallback, previewImagesSharedState, assetGroup, suggestionPoller, perfMarker, postAssetEventLog, isPMaxAspectRatiosV2Enabled, isAdPreviewRefreshEnabled]);

  const displayUrl = useMemo(() => getFinalUrlWithPath(FinalUrls, Path1, Path2), [FinalUrls, Path1, Path2]);

  const adPreview = useMemo(() => {
    const previewProps = {
      i18n,
      imageList: Images && Images.ImageList,
      updateImageList: onUpdateImageList,
      shortHeadline: Headlines && Headlines.Headline0,
      longHeadline: LongHeadlines && LongHeadlines.LongHeadline0,
      description: Descriptions && Descriptions.Description0,
      businessName: BusinessName,
      callToAction: CallToAction,
      displayUrl,
      url: first(FinalUrls),
      videos: (videos.length > selectedVideoId && selectedVideoId !== null) ? [videos[selectedVideoId]] : [],
      previewImagesSharedState,
      setPreviewImagesSharedState,
      permissions,
    };
    return (
      <PerfMaxResponsiveAdPreviewUAPL
        {...previewProps}
        logoPreviewEnabled={UAPLPMaxLogoPreviewEnabled}
        extensionsAssociations={extensionsAssociations}
        extensionFormatters={getExtensionFormatters(i18n)}
        isAdExtensionPreviewEnabled={PmaxExtensionPreview}
        isPMaxAspectRatiosV2Enabled={isPMaxAspectRatiosV2Enabled}
        isAdPreviewRefreshEnabled={isAdPreviewRefreshEnabled}
      />
    );
  }, [i18n, Images, videos, permissions, onUpdateImageList, selectedVideoId, Headlines, LongHeadlines, Descriptions, BusinessName, CallToAction, displayUrl, FinalUrls, previewImagesSharedState, UAPLPMaxLogoPreviewEnabled, extensionsAssociations, isAdPreviewRefreshEnabled, PmaxExtensionPreview, isPMaxAspectRatiosV2Enabled]);

  const isSettingPage = useMemo(() => permissions.dynamic.PmaxNorthStarAssetGroupSetting && flow === STEP_PATHS.ASSETGROUPSETTING, [flow, permissions.dynamic.PmaxNorthStarAssetGroupSetting]);

  const signalSection = useMemo(() => (
    <SignalSection
      permissions={permissions}
      assetGroup={{ data: assetGroup }}
    />
  ), [assetGroup, permissions]);

  useEffect(() => {
    if (isSaving) {
      triggerSurveyOnSubmit();
    }
  }, [isSaving, triggerSurveyOnSubmit]);

  useEffect(() => {
    if (isCanceling && _.size(themes) > 0) {
      triggerSurveyOnCancel();
    }
  }, [isCanceling, themes, triggerSurveyOnCancel]);

  useEffect(() => {
    activity.trace('[PmaxNorthStarWizard] Assets step mounted');
  }, [activity]);

  useEffect(() => {
    setClarityTag({ PMaxCampaignsCopilotRecommendations: 'true' });
  }, []);

  const onAssetGroupValidate = useCallback((result) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroup: {
          ...prevData.assetGroup,
          ...result.data,
        },
      }),
      isValid: result.isValid,
      isDirty: result.isDirty,
    });
  }, [onDataChange]);

  return (
    <ActuationGroup actuationProps={{ isActive: saveFailed }}>
      <AttentionBeaconGroup>
        <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors: saveFailed }}>
          <ValidationGroup
            validationProps={{
                schema: AssetGroup,
                onValidate: onAssetGroupValidate,
                data: assetGroup,
                i18n: mergedI18n,
              }}
          >
            <CopilotRepositoryProvider
              campaignId={Id}
              finalUrl={finalUrlForCopilotRecommendations}
              locale={i18n.getLocale()}
                // perfMarker={}
              recommendationType="PMax"
              serverContext={serverContext}
              enableLogoAndCTA={CopilotCTALogoGeneration}
            >
              <div className={classNames(getStyles('container'), 'asset-group-controls-with-name')}>
                <div className={classNames(getStyles('formContainer'), 'asset-group-controls-container')}>
                  <div className={classNames(getStyles('formHeader'))}>
                    {!false &&
                    <>
                      <h1 className="page-title">{i18n.getString(_TL_('Asset group'))}</h1>
                      <div className="page-description">
                        <span>{i18n.getString(_TL_('Performance Max campaigns consist of asset groups, which contain the information and media you want to include in your ads. We\'ll mix and match the most optimal asset combinations to create effective, high-performing ads.'))}</span>
                      </div>
                    </>
                    }
                  </div>
                  <UnmappedFormErrors className="other-server-errors" />
                  <div className="system-errors">
                    <ErrorMessages messages={systemErrors} />
                  </div>
                  {assetGroupNameSection}
                  <div className={classNames(getStyles('formContainer'), 'controls-affecting-preview-container')}>
                    {assetsSection}
                    {!isSettingPage && enhanceYourAdsSection}
                    {!isSettingPage && moreAssetsSection}
                    {moreOptionsSection}
                    {moreSettingsSection}
                    {isSettingPage && signalSection}
                  </div>
                </div>
                <div className={classNames(getStyles('previewerContainer'), 'asset-group-preview-container')}>
                  {adPreview}
                </div>
                <div className={classNames(getStyles('imageExperience'), 'asset-group-controls-container')}>
                  {imageExperienceComponents}
                </div>
              </div>
            </CopilotRepositoryProvider>
          </ValidationGroup>
          {/* {navigationConfirmationPrompt} */}
        </FormErrorGroup>
      </AttentionBeaconGroup>
    </ActuationGroup>
  );
};

AssetsStepFormInternal.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({
      Id: PropTypes.number,
      TimeZone: PropTypes.string,
    }),
    assetGroup: PropTypes.shape({
      FinalUrls: PropTypes.arrayOf(PropTypes.string),
      Images: PropTypes.shape({
        ImageList: PropTypes.arrayOf(PropTypes.string),
        ImageAssets: PropTypes.shape({}),
      }),
      CallToAction: PropTypes.shape({
        cta: PropTypes.string,
      }),
      FinalUrlForRecommendation: PropTypes.string,
      BusinessName: PropTypes.string,
      Headlines: PropTypes.shape({}),
      LongHeadlines: PropTypes.shape({}),
      Descriptions: PropTypes.shape({}),
    }),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.string),
    systemErrors: PropTypes.arrayOf(PropTypes.string),
  }),
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.string,
      aid: PropTypes.string,
    }),
  }).isRequired,
};

AssetsStepFormInternal.defaultProps = {
  error: {},
};

export const AssetsStepForm = withBannerService(withScope(AssetsStepFormInternal));
